<template>
  <div class="caja-ticket-page">
    <div class="caja-ticket-toolbar no-print">
      <button type="button" class="btn-ticket btn-print" @click="printTicket">
        <i class="fas fa-print me-1"></i> Imprimir
      </button>
      <button type="button" class="btn-ticket btn-close-tab" @click="closeTicket">
        <i class="fas fa-times me-1"></i> Cerrar
      </button>
    </div>

    <div v-if="loading" class="caja-ticket-empty no-print">
      <p>Cargando reporte...</p>
    </div>

    <div v-else-if="!report" class="caja-ticket-empty no-print">
      <p>No hay datos para mostrar.</p>
      <button type="button" class="btn-ticket btn-close-tab" @click="closeTicket">Cerrar</button>
    </div>

    <article v-else class="ticket" id="caja-ticket-print">
      <header class="ticket-header">
        <h1 class="ticket-company">{{ companyLine }}</h1>
        <p v-if="company.nit" class="ticket-sub">NIT: {{ company.nit }}</p>
        <p class="ticket-sub">Tel.: {{ company.phone || '—' }}</p>
      </header>

      <div class="ticket-sep"></div>

      <section class="ticket-doc">
        <div class="ticket-doc-title">CIERRE DE CAJA</div>
        <div class="ticket-doc-row">
          <span class="ticket-doc-number">Sesión No. {{ report.id }}</span>
          <span class="ticket-doc-date">{{ formatDate(report.closed_at || report.opened_at) }}</span>
        </div>
      </section>

      <div class="ticket-sep"></div>

      <section class="ticket-session-info">
        <div class="ticket-info-row">
          <span class="ticket-info-label">Estado:</span>
          <span class="ticket-info-value">{{ report.status === 'open' ? 'ABIERTA' : 'CERRADA' }}</span>
        </div>
        <div class="ticket-info-row">
          <span class="ticket-info-label">Abierta desde:</span>
          <span class="ticket-info-value">{{ formatDate(report.opened_at) }}</span>
        </div>
        <div v-if="report.closed_at" class="ticket-info-row">
          <span class="ticket-info-label">Cerrada en:</span>
          <span class="ticket-info-value">{{ formatDate(report.closed_at) }}</span>
        </div>
        <div class="ticket-info-row">
          <span class="ticket-info-label">Monto apertura:</span>
          <span class="ticket-info-value font-bold">{{ money(report.opening_amount) }}</span>
        </div>
        <div v-if="report.opening_notes" class="ticket-info-row">
          <span class="ticket-info-label">Nota apertura:</span>
          <span class="ticket-info-value">{{ report.opening_notes }}</span>
        </div>
      </section>

      <div class="ticket-sep"></div>

      <section class="ticket-movements">
        <div class="ticket-movements-title">MOVIMIENTOS DEL DÍA</div>
        <div class="ticket-movements-head">
          <span class="col-method">Método De Pago</span>
          <span class="col-purchases">Compras</span>
          <span class="col-sales">Ventas / Abonos</span>
        </div>
        <div class="ticket-sep-thin"></div>
        <div
          v-for="row in report.rows"
          :key="row.method"
          class="ticket-movements-row"
        >
          <span class="col-method">{{ row.method }}</span>
          <span class="col-purchases">{{ row.purchases > 0 ? money(row.purchases) : '—' }}</span>
          <span class="col-sales sales-value">{{ row.sales > 0 ? money(row.sales) : '—' }}</span>
        </div>
        <div v-if="!report.rows || report.rows.length === 0" class="ticket-movements-empty">
          Sin movimientos registrados
        </div>
        <div class="ticket-sep-thin"></div>
        <div class="ticket-movements-total">
          <span class="col-method">TOTAL</span>
          <span class="col-purchases">{{ money(report.total_purchases) }}</span>
          <span class="col-sales sales-value">{{ money(report.total_sales) }}</span>
        </div>
      </section>

      <div class="ticket-sep"></div>

      <section class="ticket-balance">
        <div class="ticket-balance-formula">
          Apertura + Ventas – Compras
        </div>
        <div class="ticket-balance-detail">
          {{ money(report.opening_amount) }} + {{ money(report.total_sales) }} – {{ money(report.total_purchases) }}
        </div>
        <div class="ticket-balance-result">
          <span>BALANCE:</span>
          <span class="balance-amount">{{ money(report.balance) }}</span>
        </div>
        <div v-if="report.closing_amount != null" class="ticket-balance-closing">
          <span>MONTO DE CIERRE:</span>
          <span class="closing-amount">{{ money(report.closing_amount) }}</span>
        </div>
        <div v-if="report.closing_notes" class="ticket-info-row mt-1">
          <span class="ticket-info-label">Nota cierre:</span>
          <span class="ticket-info-value">{{ report.closing_notes }}</span>
        </div>
      </section>

      <footer class="ticket-legal">
        <div>FRM-CAJA-001</div>
        <div>Impreso desde MiStock LBP</div>
      </footer>
    </article>
  </div>
</template>

<script>
import cashRegisterService from '@/services/cashRegister.service';
import settingsService from '@/services/settings.service';

const body = document.getElementsByTagName('body')[0];

export default {
  name: 'CashRegisterTicket',
  data() {
    return {
      report: null,
      company: {},
      loading: true,
    };
  },
  computed: {
    companyLine() {
      return String(this.company.company_name || 'MI STOCK').toUpperCase();
    },
  },
  methods: {
    money(value) {
      const n = Number(value) || 0;
      return '$ ' + n.toLocaleString('es-CO', { minimumFractionDigits: 0 });
    },
    formatDate(value) {
      if (!value) return '—';
      return new Date(value).toLocaleString('es-CO', {
        dateStyle: 'short',
        timeStyle: 'short',
      });
    },
    printTicket() {
      window.print();
    },
    closeTicket() {
      window.close();
      if (!window.closed) {
        this.$router.push({ name: 'CashRegister' });
      }
    },
    async loadReport() {
      this.loading = true;
      const sessionId = this.$route.query.id;
      try {
        const [settingsRes, reportRes] = await Promise.all([
          settingsService.get().catch(() => ({ data: {} })),
          cashRegisterService.getSessionReport(sessionId),
        ]);
        this.company = settingsRes.data || {};
        this.report = reportRes.data;
      } catch (e) {
        console.error('Error al cargar reporte de caja:', e);
        this.report = null;
      } finally {
        this.loading = false;
      }
    },
  },
  async mounted() {
    this.$store.state.hideConfigButton = true;
    this.$store.state.showSidenav = false;
    this.$store.state.showNavbar = false;
    this.$store.state.showFooter = false;
    body.classList.remove('g-sidenav-show');

    await this.loadReport();
  },
  beforeUnmount() {
    this.$store.state.hideConfigButton = false;
    this.$store.state.showSidenav = true;
    this.$store.state.showNavbar = true;
    this.$store.state.showFooter = true;
    body.classList.add('g-sidenav-show');
  },
};
</script>

<style scoped>
.caja-ticket-page {
  min-height: 100vh;
  background: #e9ecef;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.caja-ticket-toolbar {
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1rem;
  width: 80mm;
  max-width: 100%;
  justify-content: center;
}

.btn-ticket {
  border: none;
  border-radius: 6px;
  padding: 0.5rem 1.25rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  color: #fff;
}

.btn-print {
  background: #82d616;
}

.btn-close-tab {
  background: #67748e;
}

.caja-ticket-empty {
  text-align: center;
  padding: 2rem;
}

.ticket {
  width: 80mm;
  max-width: 100%;
  background: #fff;
  color: #000;
  font-family: Arial, Helvetica, sans-serif;
  font-size: 13px;
  line-height: 1.35;
  padding: 3mm 2mm;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.12);
  box-sizing: border-box;
}

.ticket-header {
  text-align: center;
  margin-bottom: 0.4rem;
}

.ticket-company {
  font-size: 14px;
  font-weight: 700;
  margin: 0 0 0.2rem;
  text-transform: uppercase;
}

.ticket-sub {
  margin: 0;
  font-size: 13px;
}

.ticket-sep {
  width: 100%;
  border-top: 1px dashed #555;
  margin: 0.35rem 0;
}

.ticket-sep-thin {
  width: 100%;
  border-top: 1px solid #ccc;
  margin: 0.2rem 0;
}

.ticket-doc {
  margin: 0.25rem 0;
  font-size: 13px;
}

.ticket-doc-title {
  font-weight: 700;
  font-size: 14px;
  text-align: center;
  margin-bottom: 0.2rem;
}

.ticket-doc-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 0.5rem;
}

.ticket-doc-number {
  flex: 1;
}

.ticket-doc-date {
  white-space: nowrap;
  flex-shrink: 0;
}

.ticket-session-info {
  margin: 0.3rem 0;
  font-size: 12px;
}

.ticket-info-row {
  display: flex;
  justify-content: space-between;
  gap: 0.3rem;
  margin-bottom: 0.1rem;
}

.ticket-info-label {
  color: #555;
  flex-shrink: 0;
}

.ticket-info-value {
  text-align: right;
}

.font-bold {
  font-weight: 700;
}

.mt-1 {
  margin-top: 0.3rem;
}

.ticket-movements {
  margin: 0.3rem 0;
  font-size: 12px;
}

.ticket-movements-title {
  font-weight: 700;
  font-size: 13px;
  margin-bottom: 0.2rem;
}

.ticket-movements-head,
.ticket-movements-row,
.ticket-movements-total {
  display: grid;
  grid-template-columns: 1fr 28% 28%;
  gap: 2px;
  align-items: center;
}

.ticket-movements-head {
  font-weight: 700;
  font-size: 11px;
  text-transform: uppercase;
}

.ticket-movements-total {
  font-weight: 700;
}

.col-purchases,
.col-sales {
  text-align: right;
}

.sales-value {
  color: #28a745;
}

.ticket-movements-empty {
  text-align: center;
  color: #888;
  font-style: italic;
  padding: 0.2rem 0;
}

.ticket-balance {
  margin: 0.3rem 0;
  font-size: 12px;
}

.ticket-balance-formula {
  font-weight: 700;
  font-size: 13px;
}

.ticket-balance-detail {
  color: #555;
  font-size: 11px;
  margin-bottom: 0.3rem;
}

.ticket-balance-result {
  display: flex;
  justify-content: space-between;
  font-weight: 700;
  font-size: 14px;
  margin-bottom: 0.15rem;
}

.balance-amount {
  font-size: 15px;
}

.ticket-balance-closing {
  display: flex;
  justify-content: space-between;
  font-weight: 700;
  font-size: 13px;
  margin-top: 0.2rem;
  border-top: 1px solid #ccc;
  padding-top: 0.2rem;
}

.closing-amount {
  color: #333;
}

.ticket-legal {
  margin-top: 1rem;
  font-size: 11px;
  line-height: 1.35;
  color: #555;
  text-align: center;
}

@media print {
  @page {
    size: 80mm auto;
    margin: 2mm;
  }

  .no-print {
    display: none !important;
  }

  .caja-ticket-page {
    background: #fff;
    padding: 0;
    min-height: auto;
  }

  .ticket {
    box-shadow: none;
    width: 80mm;
    max-width: 80mm;
    margin: 0 auto;
    font-size: 13px;
  }
}
</style>
